import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

/**
 * Captura erros de render (ex.: React #185 / loop de update) e mostra a mensagem
 * real + o componente onde ocorreu, em vez de deixar a tela preta (root desmonta).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Loga tudo no console pra inspeção/screenshot
    console.error('[ErrorBoundary]', error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: '#0b0f17',
          color: '#e5e7eb',
          fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        }}
      >
        <div
          style={{
            maxWidth: 720,
            width: '100%',
            border: '1px solid #1f2937',
            borderRadius: 12,
            padding: 24,
            background: '#111827',
          }}
        >
          <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
            Algo quebrou nesta tela
          </h1>
          <p style={{ color: '#9ca3af', fontSize: 14, marginBottom: 16 }}>
            Tire um print desta tela e envie. Os detalhes técnicos abaixo
            apontam exatamente onde ocorreu.
          </p>

          <div
            style={{
              background: '#0b0f17',
              border: '1px solid #1f2937',
              borderRadius: 8,
              padding: 12,
              fontFamily: 'ui-monospace, monospace',
              fontSize: 12,
              color: '#f87171',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              marginBottom: 12,
            }}
          >
            {error.name}: {error.message}
          </div>

          {componentStack && (
            <details style={{ marginBottom: 16 }}>
              <summary
                style={{ cursor: 'pointer', color: '#9ca3af', fontSize: 13 }}
              >
                Componente / stack
              </summary>
              <pre
                style={{
                  marginTop: 8,
                  background: '#0b0f17',
                  border: '1px solid #1f2937',
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 11,
                  color: '#9ca3af',
                  overflowX: 'auto',
                  maxHeight: 280,
                }}
              >
                {componentStack}
              </pre>
            </details>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="button"
              onClick={() => window.location.reload()}
            >
              Recarregar
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                window.location.href = '/';
              }}
            >
              Voltar ao início
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
